import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Footer from "../components/Footer";
import ImageViewer from "../components/ImageViewer";
import { useAuthStore } from "../store/authStore";
import apiClient from "../services/apiClient";
import type { ImageData } from "../components/ImageViewer";
import { useSupabase } from "../useSupabase";

// Interface for pagination data
interface Pagination {
  total: number;
  limit: number;
  offset: number;
  has_more: boolean;
}

const UserImagesPage: React.FC = () => {
  const { t } = useTranslation();
  const { isAuthenticated, token, user } = useAuthStore();
  const navigate = useNavigate();

  // State for images and loading status
  const [images, setImages] = useState<ImageData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState<Pagination>({
    total: 0,
    limit: 20,
    offset: 0,
    has_more: false,
  });

  // State for fullscreen image viewer
  const [selectedImage, setSelectedImage] = useState<ImageData | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const supabase = useSupabase();

  // Helper function to generate image URL
  const getImagePublicUrl = (imageId: string, userId: string) => {
    return `https://images.genflow.run/${userId}/${imageId}.jpg`;
  };

  // Function to fetch images directly from Supabase
  const fetchImages = useCallback(async () => {
    if (!user || !user.id) {
      setError(t("userImagesPage.errors.notAuthenticated"));
      setLoading(false);
      return;
    }

    // Track image fetch start
    window.trackEvent?.("image_fetch_started", {
      offset: pagination.offset,
      limit: pagination.limit,
    });
    setLoading(true);
    setError(null);

    try {
      // Set auth token if available
      if (token) {
        supabase.auth.setSession({
          access_token: token,
          refresh_token: "",
        });
      }

      // Count total images for pagination
      const { count: totalCount, error: countError } = await supabase
        .from("images")
        .select("id", { count: "exact", head: true })
        .eq("user_id", user.id);

      if (countError) throw countError;

      // Get images with pagination
      const { data, error: imagesError } = await supabase
        .from("images")
        .select(
          "id, width, height, job_id, is_upload, created_at, updated_at, user_id"
        )
        .eq("user_id", user.id)
        .order("created_at", { ascending: false })
        .range(pagination.offset, pagination.offset + pagination.limit - 1);

      if (imagesError) throw imagesError;

      // Process images to add URLs
      const processedImages = data.map((item) => ({
        id: item.id,
        width: item.width,
        height: item.height,
        url: getImagePublicUrl(item.id, item.user_id),
        created_at: item.created_at,
        is_upload: item.is_upload,
      }));

      // Update state
      setImages((prev) =>
        pagination.offset === 0
          ? processedImages
          : [...prev, ...processedImages]
      );

      setPagination((prev) => ({
        ...prev,
        total: totalCount || 0,
        has_more: totalCount
          ? pagination.offset + pagination.limit < totalCount
          : false,
      }));

      // Track image fetch success
      window.trackEvent?.("image_fetch_success", {
        offset: pagination.offset,
        limit: pagination.limit,
        fetchedCount: processedImages.length,
        totalCount: totalCount || 0,
        // Convert boolean to number for tracking
        hasMore: totalCount
          ? pagination.offset + pagination.limit < totalCount
            ? 1
            : 0
          : 0,
      });
    } catch (err) {
      console.error("Error fetching images:", err);
      setError(t("userImagesPage.errors.loadFailed"));
      // Track image fetch failure
      window.trackEvent?.("image_fetch_failed", {
        offset: pagination.offset,
        limit: pagination.limit,
        error: (err as Error).message || "Unknown error",
      });
    } finally {
      setLoading(false);
    }
  }, [pagination.offset, pagination.limit, token, user, t, supabase]);

  // Fetch images on component mount and when pagination changes
  useEffect(() => {
    // Redirect if not authenticated
    if (!isAuthenticated || !user) {
      navigate("/login");
      return;
    }

    // Track page view
    window.trackEvent?.("user_images_page_viewed");

    fetchImages();
  }, [
    isAuthenticated,
    pagination.offset,
    pagination.limit,
    fetchImages,
    navigate,
    user,
  ]);

  // Load more images
  const handleLoadMore = () => {
    if (pagination.has_more) {
      // Track load more clicked
      window.trackEvent?.("load_more_images_clicked", {
        currentOffset: pagination.offset,
        limit: pagination.limit,
      });
      setPagination((prev) => ({
        ...prev,
        offset: prev.offset + prev.limit,
      }));
    }
  };

  // Handle image click to show fullscreen
  const handleImageClick = (image: ImageData) => {
    setSelectedImage(image);
    // Track image view (fullscreen)
    window.trackEvent?.("image_viewed_fullscreen", { imageId: image.id });
  };

  // Close the image viewer
  const handleCloseViewer = () => {
    setSelectedImage(null);
  };

  // Navigate to a specific image ID
  const handleNavigateImage = (targetId: string) => {
    // Track navigation within image viewer
    window.trackEvent?.("image_viewer_navigated", {
      fromImageId: selectedImage?.id || "unknown",
      toImageId: targetId,
    });

    const targetIndex = images.findIndex((img) => img.id === targetId);

    if (targetIndex === -1) {
      console.warn(`Image with ID ${targetId} not found in current list.`);
      return; // Target image not found in the current list
    }

    setSelectedImage(images[targetIndex]);

    // Get the index of the *previously* selected image to determine direction for lazy loading
    const previousIndex = images.findIndex(
      (img) => img.id === selectedImage?.id
    );

    // If we're approaching the end and more images are available, load more
    // Check if navigating forward (target index is greater than previous) and near the end
    if (
      previousIndex !== -1 &&
      targetIndex > previousIndex && // Check if moving forward
      targetIndex >= images.length - 5 &&
      pagination.has_more
    ) {
      handleLoadMore();
    }
  };

  // Delete an image
  const handleDeleteImage = async (imageId: string) => {
    if (!user || !user.id || !token) {
      setDeleteError(t("userImagesPage.errors.deleteNotLoggedIn"));
      return;
    }

    // Track delete image start
    window.trackEvent?.("image_delete_started", { imageId });
    setDeleteLoading(true);
    setDeleteError(null);

    try {
      // Track delete image attempt (before API call)
      window.trackEvent?.("image_delete_attempted", { imageId });

      // Call the server-side delete endpoint
      const response = await apiClient.delete(`/api/images/${imageId}`);

      if (response.status !== 200) {
        const errorData = response.data;
        throw new Error(
          errorData.message || t("userImagesPage.errors.deleteFailed")
        );
      }

      // Remove the deleted image from the state
      setImages((prevImages) => prevImages.filter((img) => img.id !== imageId));

      // Track delete image success
      window.trackEvent?.("image_delete_success", { imageId });

      // Update pagination info
      if (pagination.total > 0) {
        setPagination((prev) => ({
          ...prev,
          total: prev.total - 1,
          has_more: prev.total - 1 > pagination.offset + images.length - 1,
        }));
      }

      // Close the viewer if the current image was deleted
      if (selectedImage?.id === imageId) {
        setSelectedImage(null);
      }
    } catch (err) {
      console.error("Error deleting image:", err);
      setDeleteError(
        err instanceof Error
          ? err.message
          : t("userImagesPage.errors.deleteFailed")
      );
      // Track delete image failure
      window.trackEvent?.("image_delete_failed", {
        imageId,
        error: err instanceof Error ? err.message : "Unknown error",
      });
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <div className="flex flex-col min-h-screen bg-black text-white">
      <main className="flex-grow container mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold mb-6">{t("userImagesPage.title")}</h1>

        {/* Button to navigate back to image creation */}
        <div style={{ marginBottom: "1.5rem", textAlign: "center" }}>
          <button
            onClick={() => {
              navigate("/home");
              // Track click on 'Create More Images'
              window.trackEvent?.("create_more_images_clicked");
            }}
            style={{
              padding: "0.75rem 1.5rem",
              backgroundColor: "#374151", // Equivalent to bg-gray-700
              color: "white",
              borderRadius: "9999px", // Equivalent to rounded-full
              fontWeight: "500", // Equivalent to font-medium
              transition: "background-color 0.3s ease",
              border: "none",
              cursor: "pointer",
              fontSize: "1rem", // Adjust as needed
              lineHeight: "1.5rem", // Adjust as needed
              maxWidth: "90%", // Ensure it fits on small screens
              boxSizing: "border-box", // Include padding in width calculation
            }}
            onMouseOver={(e) =>
              (e.currentTarget.style.backgroundColor = "#4b5563")
            } // Equivalent to hover:bg-gray-600
            onMouseOut={(e) =>
              (e.currentTarget.style.backgroundColor = "#374151")
            }
          >
            {t("userImagesPage.createMoreImages")}{" "}
            {/* Add this key to your i18n files */}
          </button>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-900 bg-opacity-50 text-red-300 rounded-lg border border-red-800">
            {error}
          </div>
        )}

        {deleteError && (
          <div className="mb-6 p-4 bg-red-900 bg-opacity-50 text-red-300 rounded-lg border border-red-800">
            {deleteError}
          </div>
        )}

        {loading && images.length === 0 ? (
          <div className="flex justify-center items-center h-64">
            <div className="text-gray-400">{t("userImagesPage.loading")}</div>
          </div>
        ) : images.length === 0 ? (
          <div className="text-center p-8 border border-gray-800 rounded-lg">
            <p className="text-gray-400 mb-4">{t("userImagesPage.noImages")}</p>
            <button
              onClick={() => navigate("/home")}
              className="px-6 py-3 bg-gray-800 text-white rounded-full hover:bg-gray-700 font-medium transition-all duration-300"
            >
              {t("userImagesPage.createFirstImage")}
            </button>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 image-grid">
              {images.map((image, index) => (
                <div
                  key={image.id}
                  className="relative aspect-square overflow-hidden rounded-lg shadow-md group cursor-pointer bg-gray-800 hover:shadow-xl transition-all duration-300 ring-2 ring-transparent hover:ring-blue-500"
                  onClick={() => handleImageClick(image)}
                >
                  <img
                    src={image.url}
                    alt={t("userImagesPage.imageAlt", { index: index + 1 })}
                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                    loading="lazy"
                  />
                  {image.created_at && (
                    <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-60 p-2 transform translate-y-full group-hover:translate-y-0 transition-transform duration-300">
                      <p className="text-xs text-gray-300">
                        {new Date(image.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  )}
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <svg
                      className="w-8 h-8 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                      ></path>
                    </svg>
                  </div>
                </div>
              ))}
            </div>

            {pagination.has_more && (
              <div className="mt-8 text-center">
                <button
                  onClick={handleLoadMore}
                  disabled={loading}
                  className="px-6 py-3 bg-gray-800 text-white rounded-full hover:bg-gray-700 font-medium shadow-lg hover:shadow-xl transition-all duration-300 disabled:opacity-50"
                >
                  {loading
                    ? t("userImagesPage.loading")
                    : t("userImagesPage.loadMoreButton")}
                </button>
              </div>
            )}
          </>
        )}
      </main>

      <Footer />

      {/* Fullscreen Image Viewer Modal - Pass delete handler and loading state */}
      {selectedImage && (
        <ImageViewer
          image={selectedImage}
          images={images}
          onClose={handleCloseViewer}
          onNavigate={handleNavigateImage}
          showNavigation={true}
          onDelete={handleDeleteImage}
          allowDelete={true}
          deleteLoading={deleteLoading}
        />
      )}
    </div>
  );
};

export default UserImagesPage;
